import { useState } from 'react';
import { MapPin, Phone, Clock, CheckCircle, AlertCircle, Mail, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    reason_for_visit: '',
    preferred_time: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [honeypot, setHoneypot] = useState('');

  const visitReasons = [
    { value: 'Erectile Dysfunction (ED)', label: 'Erectile Dysfunction (ED)' },
    { value: "Peyronie's Disease", label: "Peyronie's Disease" },
    { value: 'Low Testosterone (Low T)', label: 'Low Testosterone (Low T)' },
    { value: 'Enlarged Prostate (BPH)', label: 'Enlarged Prostate (BPH)' },
    { value: 'Vasectomy Consultation', label: 'Vasectomy Consultation' },
    { value: 'Stone Disease', label: 'Stone Disease' },
    { value: 'Voiding Dysfunction', label: 'Voiding Dysfunction' },
    { value: 'Urologic Cancer Screening', label: 'Urologic Cancer Screening' },
    { value: 'GAINSWave Therapy', label: 'GAINSWave Therapy' },
    { value: 'Other', label: 'Other' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, reason_for_visit: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check for spam
    if (honeypot) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/appointments`, {
        full_name: formData.full_name,
        phone: formData.phone,
        email: formData.email || null,
        reason_for_visit: formData.reason_for_visit,
        preferred_time: formData.preferred_time || null,
        message: formData.message || null,
      });

      // Track successful form submission
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'appointment_form_submit', { event_category: 'engagement' });
        window.gtag('event', 'appointment_form_success', { event_category: 'conversion' });
      }

      setSubmitStatus('success');
      setFormData({
        full_name: '',
        phone: '',
        email: '',
        reason_for_visit: '',
        preferred_time: '',
        message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pb-20 md:pb-0" data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Contact Havkin Urology in Melbourne, FL to schedule an appointment. Call (321) 500-4545 or request an appointment online. Located at 3021 W Eau Gallie Blvd, Suite 103."
        keywords="contact urologist Melbourne FL, schedule urology appointment, Havkin Urology phone, urology office Melbourne Florida"
        canonical="/contact"
      />
      {/* Hero */}
      <section className="py-12 md:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
              Contact
            </h1>
            <p className="text-lg text-slate-600">
              Call or request an appointment. Please don't include sensitive medical details in this form.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info & Map */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-primary mb-6">
                Office Information
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-brand/10 rounded-lg mr-4">
                    <MapPin className="h-5 w-5 text-brand" />
                  </div>
                  <div>
                    <p className="font-medium text-primary mb-1">Address</p>
                    <a 
                      href="https://maps.google.com/?q=3021+W+Eau+Gallie+Blvd+Suite+103+Melbourne+FL+32934"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-brand transition-colors"
                      data-testid="contact-address"
                    >
                      3021 W Eau Gallie Blvd, Suite 103<br />
                      Melbourne, FL 32934
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-brand/10 rounded-lg mr-4">
                    <Phone className="h-5 w-5 text-brand" />
                  </div>
                  <div>
                    <p className="font-medium text-primary mb-1">Phone</p>
                    <a 
                      href="tel:+13215004545"
                      className="text-brand font-medium hover:text-brand-hover transition-colors"
                      data-testid="contact-phone"
                    >
                      (321) 500-4545
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-brand/10 rounded-lg mr-4">
                    <Clock className="h-5 w-5 text-brand" />
                  </div>
                  <div>
                    <p className="font-medium text-primary mb-1">Office Hours</p>
                    <p className="text-slate-600">
                      Monday – Friday: 8:00 AM – 5:00 PM<br />
                      Saturday & Sunday: Closed
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-brand/10 rounded-lg mr-4">
                    <Mail className="h-5 w-5 text-brand" />
                  </div>
                  <div>
                    <p className="font-medium text-primary mb-1">Email</p>
                    <a 
                      href="mailto:drhavkin@havkinurology.com"
                      className="text-brand font-medium hover:text-brand-hover transition-colors"
                      data-testid="contact-email"
                    >
                      drhavkin@havkinurology.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-brand/10 rounded-lg mr-4">
                    <ExternalLink className="h-5 w-5 text-brand" />
                  </div>
                  <div>
                    <p className="font-medium text-primary mb-1">Instagram</p>
                    <a 
                      href="https://www.instagram.com/havkinurology/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand font-medium hover:text-brand-hover transition-colors"
                      data-testid="contact-instagram"
                    >
                      @havkinurology
                    </a>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="map-container h-64 md:h-80 rounded-xl overflow-hidden shadow-sm border border-slate-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3517.0647684661815!2d-80.67252492392!3d28.106866211548!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88de11e8d0d0e8d5%3A0x1234567890abcdef!2s3021%20W%20Eau%20Gallie%20Blvd%20%23103%2C%20Melbourne%2C%20FL%2032934!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Havkin Urology Location"
                ></iframe>
              </div>
            </div>

            {/* Appointment Form */}
            <div>
              <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
                <h2 className="font-heading text-2xl font-bold text-primary mb-6">
                  Request an Appointment
                </h2>

                {submitStatus === 'success' ? (
                  <div className="text-center py-8" data-testid="form-success">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-primary mb-2">
                      Thank You!
                    </h3>
                    <p className="text-slate-600">
                      We'll call you shortly to confirm scheduling.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot field - hidden from users */}
                    <input
                      type="text"
                      name="website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      style={{ position: 'absolute', left: '-9999px' }}
                    />

                    <div>
                      <Label htmlFor="full_name" className="text-primary">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="full_name"
                        name="full_name"
                        type="text"
                        required
                        value={formData.full_name}
                        onChange={handleChange}
                        className="mt-1.5"
                        placeholder="Your full name"
                        data-testid="form-full-name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-primary">
                        Phone <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-1.5"
                        placeholder="(555) 555-5555"
                        data-testid="form-phone"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-primary">
                        Email <span className="text-slate-400">(optional)</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-1.5"
                        placeholder="your@email.com"
                        data-testid="form-email"
                      />
                    </div>

                    <div>
                      <Label htmlFor="reason_for_visit" className="text-primary">
                        Reason for Visit <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.reason_for_visit}
                        onValueChange={handleSelectChange}
                        required
                      >
                        <SelectTrigger className="mt-1.5" data-testid="form-reason">
                          <SelectValue placeholder="Select a reason" />
                        </SelectTrigger>
                        <SelectContent>
                          {visitReasons.map((reason) => (
                            <SelectItem key={reason.value} value={reason.value}>
                              {reason.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="preferred_time" className="text-primary">
                        Preferred Day/Time <span className="text-slate-400">(optional)</span>
                      </Label>
                      <Input
                        id="preferred_time"
                        name="preferred_time"
                        type="text"
                        value={formData.preferred_time}
                        onChange={handleChange}
                        className="mt-1.5"
                        placeholder="e.g., Mornings, Tuesdays"
                        data-testid="form-preferred-time"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-primary">
                        Message <span className="text-slate-400">(optional)</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="mt-1.5 min-h-[100px]"
                        placeholder="Any additional information..."
                        data-testid="form-message"
                      />
                    </div>

                    {submitStatus === 'error' && (
                      <div className="flex items-center p-4 bg-red-50 rounded-lg text-red-700" data-testid="form-error">
                        <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                        <span>Something went wrong. Please try again or call us directly.</span>
                      </div>
                    )}

                    <p className="text-sm text-slate-500">
                      Please don't include sensitive medical details in this form.
                    </p>

                    <Button
                      type="submit"
                      disabled={isSubmitting || !formData.full_name || !formData.phone || !formData.reason_for_visit}
                      className="w-full bg-brand hover:bg-brand-hover text-white rounded-full py-3"
                      data-testid="form-submit"
                    >
                      {isSubmitting ? 'Submitting...' : 'Request Appointment'}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
